<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-5xl rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div class="flex items-center gap-4">
          <div class="p-2.5 bg-brand-600 rounded-xl shadow-lg shadow-brand-500/20">
            <Banknote class="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Recepción de Pago</h2>
            <p class="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
              CAJERO: {{ cashierName }} • RECIBO ELECTRÓNICO
            </p>
          </div>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-slate-400 hover:text-red-500 transition-colors">
          <X class="w-6 h-6" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-8 custom-scrollbar bg-slate-50/30 dark:bg-slate-950/20">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

          <div class="lg:col-span-5 space-y-6">
            <div class="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm">
              <div class="flex items-center gap-4 mb-4">
                <div class="w-12 h-12 rounded-2xl bg-brand-100 dark:bg-brand-900/30 text-brand-600 flex items-center justify-center font-black text-xl">
                  {{ (loanData?.customer_name || 'C').charAt(0) }}
                </div>
                <div>
                  <p class="text-sm font-black text-slate-900 dark:text-white uppercase leading-tight">{{ loanData?.customer_name }}</p>
                  <p class="text-[10px] font-mono text-slate-900 uppercase">CODIGO: {{ loanData?.customer_code }}</p>
                  <p class="text-[10px] font-mono text-slate-900 uppercase">CEDULA: {{ loanData?.customer_doc}}</p>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                <div>
                  <p class="text-[9px] font-black text-slate-400 uppercase">Expediente</p>
                  <p class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">{{ loanData?.code }}</p>
                </div>
                <div>
                  <p class="text-[9px] font-black text-slate-400 uppercase">Producto</p>
                  <p class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">{{ loanData?.product_name }}</p>
                </div>
                <div>
                  <p class="text-[9px] font-black text-slate-400 uppercase">Tasa Anual</p>
                  <p class="text-xs font-bold text-brand-600 dark:text-brand-400">{{ loanData?.rate }}% ({{ loanData?.interest_type }})</p>
                </div>
              </div>
            </div>

            <div class="bg-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
              <Wallet class="absolute -right-4 -bottom-4 w-24 h-24 text-white opacity-5" />
              <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Saldo Capital Actual</p>
              <p class="text-4xl font-black font-mono tracking-tighter">${{ formatNum(currentPrincipal) }}</p>

              <div class="mt-6 pt-4 border-t border-white/10 space-y-3">
                <div class="flex justify-between text-xs">
                  <span class="text-slate-400 font-bold uppercase text-[9px]">Último Movimiento</span>
                  <span class="font-mono text-slate-300">{{ lastDateFormatted }}</span>
                </div>
                <div class="flex justify-between text-xs">
                  <span class="text-slate-400 font-bold uppercase text-[9px]">
                    {{ isFlat ? 'Tipo de Amortización' : 'Días de Interés Pendiente' }}
                  </span>
                  <span class="font-black text-amber-400">
                    {{ isFlat ? 'CUOTA FLAT' : `${daysElapsed} Días` }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="lg:col-span-7">
            <form id="paymentForm" @submit.prevent="handleSubmit" class="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-lg h-full flex flex-col relative">

              <div v-if="isLoadingPlan" class="absolute inset-0 z-10 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm flex items-center justify-center rounded-3xl">
                <Loader2 class="w-8 h-8 animate-spin text-brand-600" />
              </div>

              <div class="mb-8">
                <label class="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 block">Monto Recibido del Cliente ($)</label>
                <div class="relative">
                  <span class="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-black text-slate-400">$</span>
                  <input
                    v-model="paymentAmount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    class="w-full pl-12 pr-6 py-4 bg-slate-50 dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 focus:border-emerald-500 rounded-[1.5rem] text-3xl font-black font-mono outline-none transition-all dark:text-white"
                    placeholder="0.00"
                  >
                </div>
                <div class="flex gap-2 mt-3">
                  <button type="button" @click="paymentAmount = suggestedQuota" class="px-4 py-2 bg-brand-50 text-white dark:bg-brand-900/30 dark:text-white text-[10px] font-black rounded-xl uppercase transition-colors hover:bg-brand-100 shadow-sm border border-brand-100 dark:border-brand-800">Cuota Exacta: ${{ formatNum(suggestedQuota) }}</button>
                  <button type="button" @click="paymentAmount = activeTotalPayoff" class="px-4 py-2 bg-brand-50 text-white dark:bg-brand-900/30 dark:text-white text-[10px] font-black rounded-xl uppercase transition-colors hover:bg-brand-100 shadow-sm border border-brand-100 dark:border-brand-800">Cancelar Totalidad: ${{ formatNum(activeTotalPayoff) }}</button>
                </div>
              </div>

              <div class="flex-1 bg-slate-50 dark:bg-slate-900 rounded-[1.5rem] p-6 border border-slate-100 dark:border-slate-700 mb-8 space-y-4">
                <h4 class="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-200 dark:border-slate-700 pb-2 flex justify-between">
                  <span>Distribución del Dinero</span>
                  <span v-if="lateFeeAmount > 0" class="text-red-500">Mora: ${{ formatNum(lateFeeAmount) }}</span>
                </h4>

                <div class="flex justify-between items-center text-sm">
                  <span class="font-bold text-slate-600 dark:text-slate-400">
                    {{ isFlat ? 'Interés a Cobrar (Flat / Cuotas)' : `Interés a Cobrar (${daysElapsed} días)` }}
                  </span>
                  <span class="font-mono font-black text-amber-600">${{ formatNum(activeInterestToApply) }}</span>
                </div>
                <div class="flex justify-between items-center text-sm">
                  <span class="font-bold text-slate-600 dark:text-slate-400">Abono Directo a Capital</span>
                  <span class="font-mono font-black text-emerald-600">${{ formatNum(activePrincipalToApply) }}</span>
                </div>

                <div class="pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center mt-2">
                  <span class="font-black text-slate-800 dark:text-white uppercase text-xs">Nuevo Saldo Capital Proyectado</span>
                  <span class="font-mono font-black text-xl text-slate-900 dark:text-white">${{ formatNum(activeNewBalance) }}</span>
                </div>

                <div v-if="paymentAmount > 0 && activePrincipalToApply <= 0" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50 flex items-start gap-3">
                  <AlertCircle class="w-5 h-5 text-red-500 shrink-0" />
                  <p class="text-[10px] font-bold text-red-700 dark:text-red-400 uppercase mt-0.5">Atención: El pago no cubre el interés ni la mora. El capital quedará intacto.</p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="text-[9px] font-black text-slate-400 uppercase mb-1 block">Mora / Cargos ($)</label>
                  <input v-model="lateFeeAmount" type="number" step="0.01" min="0" placeholder="0.00" class="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none dark:text-white transition-all text-red-600 dark:text-red-400">
                </div>
                <div>
                  <label class="text-[9px] font-black text-slate-400 uppercase mb-1 block">Método de Pago</label>
                  <select v-model="paymentMethod" class="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none dark:text-white transition-all">
                    <option value="EFECTIVO">Efectivo</option>
                    <option value="TRANSFERENCIA">Transferencia</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
                <div>
                  <label class="text-[9px] font-black text-slate-400 uppercase mb-1 block">Referencia</label>
                  <input v-model="paymentNotes" type="text" placeholder="No. Doc..." class="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none dark:text-white transition-all">
                </div>
              </div>

            </form>
          </div>

        </div>
      </div>

      <div class="px-8 py-5 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
        <p class="text-[9px] text-slate-400 font-black uppercase tracking-[0.2em]"></p>
        <div class="flex gap-4">
          <button @click="$emit('close')" type="button" class="px-6 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-colors border border-transparent dark:hover:border-slate-700">Cancelar Op.</button>
          <button type="submit" form="paymentForm" :disabled="isSaving || paymentAmount <= 0"
            class="px-10 py-3 text-[10px] font-black text-white bg-brand-600 hover:bg-brand-700 shadow-lg shadow-brand-500/30 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2 uppercase tracking-widest">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <CheckCircle v-else class="w-4 h-4" />
            Aplicar Transacción
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { X, Loader2, Banknote, Wallet, AlertCircle, CheckCircle } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import { useTicketPrint } from '@/composables/cash/useTicketPrint';
const { generateTicket } = useTicketPrint();
const props = defineProps({ show: Boolean, loanData: Object });
const emit = defineEmits(['close', 'success']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);
const paymentAmount = ref('');
const paymentMethod = ref('EFECTIVO');
const paymentNotes = ref('');
const lateFeeAmount = ref(0);

const activePaymentPlans = ref([]);
const isLoadingPlan = ref(false);

const cashierName = computed(() => SecureStorage.getItem('llevo_user')?.full_name || 'CAJERO');
const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isFlat = computed(() => props.loanData?.interest_type === 'FLAT');

// ============================================================================
// CARGA DEL PLAN Y MATEMÁTICA BASE
// ============================================================================

watch(() => props.show, async (val) => {
  if (val && props.loanData?.id) {
    paymentAmount.value = '';
    paymentNotes.value = '';
    paymentMethod.value = 'EFECTIVO';
    lateFeeAmount.value = 0;
    activePaymentPlans.value = [];

    isLoadingPlan.value = true;
    try {
      const res = await apiClient.get(`/payment_plans?loan_id=${props.loanData.id}&status=PENDING`);
      activePaymentPlans.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
    } catch (error) {
      console.error("Error al cargar plan de pagos:", error);
    } finally {
      isLoadingPlan.value = false;
    }
  }
});

const nextInstallment = computed(() => {
    return activePaymentPlans.value.length > 0 ? activePaymentPlans.value[0] : null;
});

const currentPrincipal = computed(() => {
  return Number(props.loanData?.remaining_balance || props.loanData?.remaining_capital || props.loanData?.amount || 0);
});

const daysElapsed = computed(() => {
  if (!props.loanData) return 0;
  const lastDateStr = props.loanData.last_payment_date || props.loanData.disbursed_at || props.loanData.created_at;
  if (!lastDateStr) return 1;

  const lastDate = new Date(lastDateStr);
  const today = new Date();

  lastDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = Math.abs(today - lastDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
});

const suggestedQuota = computed(() => {
  if (!props.loanData) return 0;

  if (nextInstallment.value && nextInstallment.value.total_amount) {
      return Number(nextInstallment.value.total_amount) + Number(lateFeeAmount.value);
  }

  if (props.loanData.estimated_quota && Number(props.loanData.estimated_quota) > 0) {
    return Number(props.loanData.estimated_quota) + Number(lateFeeAmount.value);
  }

  const p = Number(props.loanData.amount || 0);
  const rate = Number(props.loanData.rate || 0);
  const term = Number(props.loanData.term || 1);
  const isSobreSaldo = props.loanData.interest_type === 'SOBRE SALDO' || props.loanData.interest_type === 'SOBRE_SALDO' || props.loanData.interest_type === 'NORMAL';

  if (p === 0) return 0;

  let base = 0;
  if (isSobreSaldo) {
    const i = (rate / 100) / 12;
    if (i === 0) {
      base = p / term;
    } else {
      const factor = Math.pow(1 + i, term);
      base = p * ((i * factor) / (factor - 1));
    }
  } else {
    const totalInterest = p * (rate / 100) * (term / 12);
    base = (p + totalInterest) / term;
  }

  return base + Number(lateFeeAmount.value);
});

const lastDateFormatted = computed(() => {
  const dStr = props.loanData?.last_payment_date || props.loanData?.disbursed_at || props.loanData?.created_at;
  return dStr ? new Date(dStr).toLocaleDateString('es-ES') : 'Desconocida';
});


// ============================================================================
// 1. LÓGICA NORMAL / SOBRE SALDO (INTACTA)
// ============================================================================
const accruedInterestExact = computed(() => {
  if (!props.loanData || !nextInstallment.value) return 0;

  const dueDate = new Date(nextInstallment.value.due_date);
  const today = new Date();

  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (today.getTime() === dueDate.getTime()) {
      return Number(nextInstallment.value.interest);
  }

  const annualRate = Number(props.loanData.rate || 0);
  const dailyRate = (annualRate / 100) / 365;
  return currentPrincipal.value * dailyRate * daysElapsed.value;
});

const totalPayoff = computed(() => currentPrincipal.value + accruedInterestExact.value + Number(lateFeeAmount.value));

const interestToApply = computed(() => {
  let availableMoney = Number(paymentAmount.value || 0);
  availableMoney -= Number(lateFeeAmount.value);
  if(availableMoney <= 0) return 0;

  const accrued = accruedInterestExact.value;
  return availableMoney >= accrued ? accrued : availableMoney;
});

const principalToApply = computed(() => {
  let availableMoney = Number(paymentAmount.value || 0);
  const remainder = availableMoney - Number(lateFeeAmount.value) - interestToApply.value;

  if (remainder > currentPrincipal.value) return currentPrincipal.value;
  return remainder > 0 ? remainder : 0;
});

const newBalance = computed(() => {
  const bal = currentPrincipal.value - principalToApply.value;
  return bal > 0 ? bal : 0;
});


// ============================================================================
// 2. NUEVA LÓGICA EXCLUSIVA PARA FLAT (Cascada Exacta Cuota a Cuota)
// ============================================================================
const totalRemainingInterestFlat = computed(() => {
  if (!activePaymentPlans.value.length) return 0;
  return activePaymentPlans.value.reduce((sum, q) => sum + Number(q.interest || 0), 0);
});

// En FLAT, el interés de la siguiente cuota es contractual (NO se inventa por días transcurridos)
const accruedInterestExactFlat = computed(() => {
  if (!props.loanData || !nextInstallment.value) return 0;
  return Number(nextInstallment.value.interest || 0);
});

const totalPayoffFlat = computed(() => {
  // Cancelar FLAT implica pagar el saldo de capital + TODO el interés pendiente de las cuotas
  return currentPrincipal.value + totalRemainingInterestFlat.value + Number(lateFeeAmount.value || 0);
});

// Simulación en cascada: distribuye el abono cuota por cuota (primero interés, luego capital)
const flatPaymentDistribution = computed(() => {
  let money = Number(paymentAmount.value || 0) - Number(lateFeeAmount.value || 0);
  if (money <= 0 || !activePaymentPlans.value.length) {
    return { interest: 0, principal: 0 };
  }

  let totalInt = 0;
  let totalCap = 0;

  for (const quota of activePaymentPlans.value) {
    if (money <= 0.001) break;

    const qCap = Number(quota.capital || 0);
    const qInt = Number(quota.interest || 0);

    // 1. Prelación de cobro: Primero cubrimos el interés de la cuota
    const payInt = Math.min(money, qInt);
    totalInt += payInt;
    money -= payInt;

    // 2. Luego cubrimos el capital de la cuota
    const payCap = Math.min(money, qCap);
    totalCap += payCap;
    money -= payCap;
  }

  return {
    interest: Number(totalInt.toFixed(2)),
    principal: Number(totalCap.toFixed(2))
  };
});

const interestToApplyFlat = computed(() => flatPaymentDistribution.value.interest);
const principalToApplyFlat = computed(() => flatPaymentDistribution.value.principal);

const newBalanceFlat = computed(() => {
  const bal = currentPrincipal.value - principalToApplyFlat.value;
  return bal > 0 ? bal : 0;
});


// ============================================================================
// 3. INTERRUPTORES INTELIGENTES PARA LA INTERFAZ
// ============================================================================
const activeTotalPayoff = computed(() => isFlat.value ? totalPayoffFlat.value : totalPayoff.value);
const activeInterestToApply = computed(() => isFlat.value ? interestToApplyFlat.value : interestToApply.value);
const activePrincipalToApply = computed(() => isFlat.value ? principalToApplyFlat.value : principalToApply.value);
const activeNewBalance = computed(() => isFlat.value ? newBalanceFlat.value : newBalance.value);


const handleSubmit = async () => {
  const amount = Number(paymentAmount.value);
  if (amount <= 0) return showError('El monto debe ser mayor a 0.');

  isSaving.value = true;
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  const mysqlDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  try {
    const payload = {
      loan_id: props.loanData.id,
      company_id: SecureStorage.getItem('llevo_user')?.company,
      amount_paid: amount,
      principal_applied: activePrincipalToApply.value, // <--- CASCADA EXACTA EN FLAT
      interest_applied: activeInterestToApply.value,   // <--- CASCADA EXACTA EN FLAT
      late_fee_applied: Number(lateFeeAmount.value),
      payment_method: paymentMethod.value,
      notes: paymentNotes.value,
      payment_date: mysqlDate,
      days_calculated: nextInstallment.value ? Number(nextInstallment.value.days) : daysElapsed.value,
      cashier_id: SecureStorage.getItem('llevo_user')?.id
    };

    const response = await apiClient.post('/payments', payload);
    showSuccess('Cobro ingresado con éxito.');
    const { transaction_id } = response.data;
    generateTicket({
      payment_date: new Date().toLocaleString('es-ES'),
      cashier_name: cashierName.value,
      transaction_id: transaction_id,
      loan_code: props.loanData.code,
      customer_name: props.loanData.customer_name,
      interest_type: props.loanData.interest_type,
      amount_paid: amount,
      principal_applied: activePrincipalToApply.value,
      interest_applied: activeInterestToApply.value,
      late_fee_applied: Number(lateFeeAmount.value),
      new_balance: activeNewBalance.value
    });
    emit('success');
  } catch (e) {
    showError(e.response?.data?.message || 'Error registrando el pago en la caja.');
  } finally {
    isSaving.value = false;
  }
};
</script>